import { useCallback, useEffect, useState } from "react";
import { tr } from "./i18n.js";
import { cuConflict, deUrcat, sincronizeazaTot, uitaFisa } from "./offline/coada.js";
import { potTineLocal, SEMNAL } from "./offline/db.js";

/**
 * MT19 — ce mai are telefonul de urcat.
 *
 * Stă în bara de sus, lângă clopoțel, fiindcă e chiar întrebarea pe care și-o
 * pune omul la ieșirea din hală: „a plecat ce am lucrat?". Nu apare deloc când
 * nu e nimic în coadă — o pastilă permanentă care spune „0" ar fi zgomot.
 */
export default function IndicatorTeren() {
  const [inCoada, setInCoada] = useState([]);
  const [conflicte, setConflicte] = useState([]);
  const [deschis, setDeschis] = useState(false);
  const [lucreaza, setLucreaza] = useState(false);
  const [potLocal, setPotLocal] = useState(true);
  const [online, setOnline] = useState(
    typeof navigator === "undefined" ? true : navigator.onLine);

  const reciteste = useCallback(async () => {
    try {
      setInCoada(await deUrcat());
      setConflicte(await cuConflict());
    } catch { /* fereastră privată: se tratează mai jos, prin `potLocal` */ }
  }, []);

  const urca = useCallback(async () => {
    setLucreaza(true);
    try { await sincronizeazaTot(); } finally { setLucreaza(false); await reciteste(); }
  }, [reciteste]);

  useEffect(() => { potTineLocal().then(setPotLocal); reciteste(); }, [reciteste]);

  // Coada se schimbă din ecranul de teren, nu de aici: indicatorul ascultă.
  useEffect(() => {
    const la = () => reciteste();
    window.addEventListener(SEMNAL, la);
    return () => window.removeEventListener(SEMNAL, la);
  }, [reciteste]);

  /*
   * La revenirea semnalului se urcă SINGUR. Ăsta e chiar rostul modulului: omul
   * a ieșit din hală, a prins semnal în parcare, iar fișa pleacă fără ca el să-și
   * amintească de un buton.
   */
  useEffect(() => {
    const laOnline = () => { setOnline(true); urca(); };
    const laOffline = () => setOnline(false);
    window.addEventListener("online", laOnline);
    window.addEventListener("offline", laOffline);
    // Și periodic, fiindcă „online" înseamnă doar că există o rețea, nu că
    // serverul răspunde: o hală cu semnal slab dă exact cazul ăsta.
    const ceas = setInterval(() => { if (navigator.onLine) urca(); }, 60000);
    return () => {
      window.removeEventListener("online", laOnline);
      window.removeEventListener("offline", laOffline);
      clearInterval(ceas);
    };
  }, [urca]);

  if (potLocal && !inCoada.length && !conflicte.length && online) return null;

  const total = inCoada.length + conflicte.length;

  return (
    <>
      <button
        className={conflicte.length ? "pastila-teren pastila-teren-atentie" : "pastila-teren"}
        onClick={() => setDeschis(true)}
        title={tr("Fișe lucrate fără semnal")}
      >
        {!potLocal
          ? tr("fără memorie locală")
          : !online && !total
            ? tr("fără semnal")
            : `${total} ${total === 1 ? tr("fișă de urcat") : tr("fișe de urcat")}`}
      </button>

      {deschis && (
        <div className="modal-fundal" onClick={(e) => { if (e.target === e.currentTarget) setDeschis(false); }}>
          <div className="modal modal-lat">
            <h3>{tr("Fișe lucrate fără semnal")}</h3>

            {!potLocal && (
              <p className="eroare">
                {tr("Browserul nu lasă aplicația să țină date local (fereastră privată sau date de sit blocate). Lucrează cu semnal — altfel fișa nu se poate salva pe teren.")}
              </p>
            )}

            {!online && (
              <p className="nota">
                {tr("Nu e semnal acum. Fișele rămân în telefon și pleacă singure când revine.")}
              </p>
            )}

            {conflicte.length > 0 && (
              <div className="panou">
                <div className="panou-antet">{tr("Au nevoie de tine")}</div>
                <div className="panou-corp">
                  {conflicte.map((f) => (
                    <div key={f.localId} className="bloc-vizita">
                      <p>{f.explicatie}</p>
                      {f.numarRezultat && (
                        <p className="nota">
                          {tr("Munca ta a fost păstrată pe fișa")} <code>{f.numarRezultat}</code>
                        </p>
                      )}
                      <div className="linie-actiune">
                        <button className="buton-mic" onClick={async () => {
                          await uitaFisa(f.localId); await reciteste();
                        }}>{tr("am înțeles, scoate-o din listă")}</button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {inCoada.length > 0 && (
              <div className="panou">
                <div className="panou-antet">{tr("De urcat")}</div>
                <div className="panou-corp">
                  {inCoada.map((f) => (
                    <div key={f.localId} className="bloc-vizita">
                      <p>
                        {f.vizitaNoua?.scopInterventie
                          || f.descriereConstatari
                          || tr("fișă începută pe teren")}
                      </p>
                      <p className="nota">
                        {tr("lucrată la")} {String(f.creataLa).slice(0, 16).replace("T", " ")}
                        {/* Numărul îl dă serverul la urcare: două telefoane offline
                            ar da altfel același număr la clienți diferiți. */}
                        {" · "}{tr("se numerotează la sincronizare")}
                      </p>
                      {f.ultimaEroare && (
                        <p className="eroare-mica">
                          {tr("ultima încercare a eșuat")}: {f.ultimaEroare}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {!inCoada.length && !conflicte.length && (
              <p className="nota">{tr("Nimic de urcat. Tot ce ai lucrat a ajuns pe server.")}</p>
            )}

            <div className="modal-actiuni">
              <button className="buton buton-sters" onClick={() => setDeschis(false)}>
                {tr("închide")}
              </button>
              {inCoada.length > 0 && (
                <button className="buton" disabled={lucreaza || !online} onClick={urca}>
                  {lucreaza ? tr("se urcă…") : tr("urcă acum")}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
